import { FormEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { supabase } from '../supabase';

const PASSWORD_MIN_LENGTH = 8;

type Status = 'loading' | 'ready' | 'saving' | 'done' | 'error';

function ResetPasswordPage() {
  const [status, setStatus] = useState<Status>('loading');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Native apps only kick off the reset email; this page is the only
  // place a new password is chosen. supabase-js consumes the recovery
  // hash on createClient (detectSessionInUrl: true by default) and fires
  // INITIAL_SESSION / PASSWORD_RECOVERY through onAuthStateChange. If no
  // session shows up within a few seconds, the link is bad or missing.
  useEffect(() => {
    let cancelled = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) setStatus('ready');
    });

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      setStatus((prev) => (prev === 'loading' ? 'error' : prev));
    }, 3000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const hasMinLength = password.length >= PASSWORD_MIN_LENGTH;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const isPasswordStrong =
    hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isPasswordStrong || status === 'saving') return;
    setStatus('saving');
    setErrorMessage(null);

    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setErrorMessage(error.message);
      setStatus('ready');
      return;
    }

    // Keep the web site read-only — drop the recovery session rather than
    // leaving the user silently signed in in the browser.
    await supabase.auth.signOut();
    setStatus('done');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flex: 1, py: 4 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Reset Password
        </Typography>

        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {status === 'error' && (
          <Alert severity="error">
            This reset link is invalid or has expired. Open Third Place and tap
            "Forgot password?" again to get a fresh link.
          </Alert>
        )}

        {status === 'done' && (
          <Alert severity="success">
            Your password has been updated. Open Third Place and log in with your new password.
          </Alert>
        )}

        {(status === 'ready' || status === 'saving') && (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Choose a new password for your account.
            </Typography>

            <TextField
              type="password"
              label="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={status === 'saving'}
              autoComplete="new-password"
              fullWidth
            />

            <Stack spacing={0.5} sx={{ mt: 1.5 }}>
              <Requirement met={hasMinLength} label={`At least ${PASSWORD_MIN_LENGTH} characters`} />
              <Requirement met={hasUppercase} label="One uppercase letter" />
              <Requirement met={hasLowercase} label="One lowercase letter" />
              <Requirement met={hasDigit} label="One number" />
              <Requirement met={hasSymbol} label="One symbol" />
            </Stack>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={!isPasswordStrong || status === 'saving'}
              sx={{ mt: 3, height: 48 }}
              fullWidth
            >
              {status === 'saving' ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Update password'
              )}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

function Requirement({ met, label }: { met: boolean; label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{ color: met ? 'success.main' : 'text.secondary' }}
    >
      {met ? '✓' : '○'}  {label}
    </Typography>
  );
}

export default ResetPasswordPage;
